// Editor de regra de desembolso (espec. 8.3). Offcanvas prop-driven no padrão do
// `DisbursementCoverageOffcanvas`, com o que a espec. 8.5 pede por cima: `role="dialog"`,
// foco que entra ao abrir e volta ao fechar, e fechamento por Escape.
//
// Este arquivo é a máquina de estado: alvo, atalho, formulário, linhas, validação e
// salvar. A apresentação está em três irmãos — `RuleTargetPicker` (passo 1, mais o
// contrato do alvo), `RuleShortcutFields` e `RuleProfileTable` (passo 2).
//
// Passo 3 (Task 17): a pré-visualização é LOCAL (`RulePreviewChart` + `previewTargetCurve`)
// e o salvar fala com a rota por aqui — a menos que o chamador passe `onSave`, caso em
// que o editor só delega (contrato da Task 16, para quem já grava por conta).
import { useEffect, useMemo, useRef, useState } from "react";
import { isPresentationProject } from "@utils/presentationMode";

import DescribeRuleWithAi from "./ai/DescribeRuleWithAi";
import { confirmRuleWithAi, newIdempotencyKey } from "./ai/disbursementAiApi";
import {
  fetchDisbursementTargets,
  saveDisbursementRule,
} from "./disbursementApi";
import { EDITOR_MODES } from "./editorModes";
import { disbursementErrorDetail } from "./errorMessages";
import { competenceText } from "./format";
import MonthEditTable from "./MonthEditTable";
import { currentCompetence, monthsText, seriesPairs } from "./monthEdit";
import {
  AT_SIGHT_PROFILE,
  MAX_LINES,
  ProfileError,
  describeProfile,
  validateProfile,
} from "./profile";
import { ORIGIN_NAMES } from "./OriginSelector";
import RuleImpactSection from "./RuleImpactSection";
import RuleOverridesSection, {
  OVERRIDE_DECISIONS,
} from "./RuleOverridesSection";
import RulePreviewChart from "./RulePreviewChart";
import RuleProfileTable, {
  PROFILE_ERROR_ID,
  seedLines,
  withLineId,
} from "./RuleProfileTable";
import RuleShortcutFields, { buildShortcutProfile } from "./RuleShortcutFields";
import useMonthEdit from "./useMonthEdit";
import useRuleImpact, { IMPACT_DEBOUNCE_MS, requiresImpactPreview } from "./useRuleImpact";
import RuleTargetPicker, {
  TargetValue,
  normalizeTarget,
  ruleTargetLevel,
  ruleTargetOf,
  targetQueryLevel,
} from "./RuleTargetPicker";

// O contrato do alvo mora no `RuleTargetPicker`, mas a Task 17 o consome pelo editor.
export { normalizeTarget, ruleTargetLevel, ruleTargetOf, targetQueryLevel };

const SEARCH_DEBOUNCE_MS = 350;
const SAVE_ERROR_TEXT = "Não foi possível salvar a regra. Tente novamente.";
const SAVE_ERROR_PREFIX = "Não foi possível salvar a regra.";
// A obra mudou entre a prévia de impacto e a confirmação: nada foi gravado e a prévia é
// recalculada sozinha, para o cliente confirmar sobre o que de fato vai acontecer.
const STALE_PREVIEW_TEXT =
  "A obra mudou depois da prévia de impacto. A prévia foi recalculada; confira o novo impacto antes de reprogramar.";

// `conflictingGroup` chega como objeto `{ groupId, name }` (contrato da Task 13) — a
// string crua é só a forma tolerada. Interpolar o objeto direto imprimiria
// "[object Object]" na cara do cliente.
const conflictingGroupName = (group) =>
  typeof group === "string" ? group : group?.name;

const overlapText = (group) => {
  const name = conflictingGroupName(group);
  return name
    ? `Este alvo já está coberto pelo grupo "${name}".`
    : "Este alvo já está coberto por outro grupo.";
};

const saveErrorText = (result) => {
  const errorCode = result?.errorCode;
  if (errorCode === "impact_preview_stale") return STALE_PREVIEW_TEXT;
  if (errorCode === "group_overlap") {
    return `${SAVE_ERROR_PREFIX} ${overlapText(result?.conflictingGroup)}`;
  }
  // Vocabulário do `rulePlanner`/`rulesStore`: `invalid_target` e `rule_not_found`.
  // Não existe `target_invalid`/`target_not_found` no back-end — mapear aqueles
  // deixaria o cliente com a frase genérica justamente nos erros de alvo.
  const detail = disbursementErrorDetail(errorCode);
  return detail ? `${SAVE_ERROR_PREFIX} ${detail}` : SAVE_ERROR_TEXT;
};

// Os dois modos de um alvo da EAP (D20); reexportado para quem já importa o editor.
export { EDITOR_MODES };

const MONTH_SAVE_ERROR_TEXT = "Não foi possível salvar o ajuste. Tente novamente.";
const MONTH_SAVE_ERROR_PREFIX = "Não foi possível salvar o ajuste.";

const monthSaveErrorText = (result) => {
  const errorCode = result?.errorCode;
  if (errorCode === "month_edit_stale") {
    return "A obra mudou depois que o ajuste foi aberto. A referência foi recarregada; confira os meses antes de salvar.";
  }
  if (errorCode === "month_edit_conflict") {
    const count = result?.details?.conflicts?.length ?? 0;
    return `${MONTH_SAVE_ERROR_PREFIX} ${
      count > 0 ? `${count === 1 ? "Um ajuste" : `${count} ajustes`} de um nível acima ou abaixo` : "Outro ajuste"
    } disputa o valor desses meses; remova-o antes de ajustar aqui.`;
  }
  if ((errorCode === "month_edit_unbalanced" || errorCode === "invalid_month_edit") && result?.message) {
    return `${MONTH_SAVE_ERROR_PREFIX} ${result.message}`;
  }
  const detail = disbursementErrorDetail(errorCode);
  return detail ? `${MONTH_SAVE_ERROR_PREFIX} ${detail}` : MONTH_SAVE_ERROR_TEXT;
};

const MONTH_UNAVAILABLE_TEXT =
  "Não dá para ajustar este alvo agora: a obra não tem uma curva de origem utilizável.";

const DEFAULT_FORM = Object.freeze({
  unit: "months",
  installments: 1,
  startOffset: 0,
  downPaymentBps: 3000,
  downPaymentOffset: -1,
});

const profileErrorText = (error) =>
  error instanceof ProfileError
    ? error.message
    : "Forma de desembolso inválida";

const RuleEditorOffcanvas = ({
  open,
  onClose,
  project = null,
  projectId = null,
  target = null,
  initialTarget = null,
  profile = null,
  initialRule = null,
  initialSource = "manual",
  rulesByRuleId = null,
  readOnly = false,
  onChangeTarget,
  onChangeProfile,
  onSave,
  onSaved,
  // A origem do desembolso da obra (`state.origin`): nomeia a série de comparação da
  // pré-visualização e a origem vigente no cabeçalho do alvo.
  origin = null,
  impactDebounceMs = IMPACT_DEBOUNCE_MS,
  // Abre a regra de um insumo/grupo que governa parte do alvo (lista de exceções).
  onEditOverride,
  // D20: abrir já no ajuste mês a mês (editar o ajuste de um alvo pela lista ou pela EAP).
  initialMode = EDITOR_MODES.schedule,
  // A competência de hoje, para destacar os meses passados na tabela do ajuste.
  currentMonth = null,
}) => {
  const workId = projectId ?? project?._id ?? null;
  const seededTarget = useMemo(
    () => normalizeTarget(target ?? initialTarget),
    [target, initialTarget],
  );
  const seededProfile = profile ?? initialRule?.profile ?? null;

  const [targetType, setTargetType] = useState(
    (target ?? initialTarget)?.kind === "node" ? "node" : "erp_group",
  );
  const [nodeLevel, setNodeLevel] = useState("substep");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [loadingTargets, setLoadingTargets] = useState(false);
  const [targetsError, setTargetsError] = useState(false);
  const [chosenTarget, setChosenTarget] = useState(seededTarget);
  // Semear a partir de uma regra existente cai em "Personalizado": salvar de novo sem
  // tocar em nada tem que preservar exatamente o perfil gravado, não regenerá-lo.
  const [shortcut, setShortcut] = useState(seededProfile ? "custom" : "equal");
  const [form, setForm] = useState(DEFAULT_FORM);
  const [customLines, setCustomLines] = useState(() =>
    seedLines(seededProfile),
  );
  const [description, setDescription] = useState(
    initialRule?.description ?? "",
  );
  // Início do novo cronograma ("YYYY-MM"). Vazio = o primeiro mês do alvo na origem, que é
  // o que o motor usa para uma regra sem início; o que vai gravado é sempre o efetivo.
  const [startMonth, setStartMonth] = useState(initialRule?.startMonth ?? "");
  const [descriptionDirty, setDescriptionDirty] = useState(false);
  const [aiDraft, setAiDraft] = useState(null);
  const confirmationRequest = useRef(null);
  const confirmedResult = useRef(null);
  // Origem do perfil gravado (espec. 10.3): a IA preenche com `"ai_assistant"`, e
  // QUALQUER edição manual depois — atalho, linha, campo do atalho — volta para
  // `"manual"`. Sem isso o rótulo de origem na lista mentiria sobre um perfil que o
  // cliente reescreveu depois da proposta.
  const [ruleSource, setRuleSource] = useState(initialSource);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  // Série do previsto buscada quando o alvo chega SEM ela (linha da lista de regras
  // não carrega `plannedSeries`): guardada junto com a chave do alvo, pra nunca
  // vazar o previsto de um alvo para outro, e com o `status` da busca
  // (`loading`/`ready`/`failed`) — "ainda não chegou" e "não tem" são coisas
  // diferentes na tela.
  const [hydrated, setHydrated] = useState(null);
  const [overwriteWarning, setOverwriteWarning] = useState(false);
  // D19: com insumos de regra própria dentro do alvo, o usuário escolhe se eles mantêm a
  // forma deles (`keep`) ou passam a seguir esta regra (`override`). `null` = não escolheu.
  const [overrideDecision, setOverrideDecision] = useState(null);
  // D20: cronograma novo (regra) ou ajuste mês a mês. Só um alvo de EAP tem os dois.
  const [mode, setMode] = useState(initialMode);

  const panelRef = useRef(null);
  const restoreFocusRef = useRef(null);
  // A identidade do alvo semeado é a CHAVE, não o objeto: um pai que remonta a prop a
  // cada render (ou recarrega o mesmo alvo da rota) não pode resetar o editor.
  const seedKey = JSON.stringify([
    workId,
    seededTarget?.key ?? null,
    seededProfile,
    initialSource,
    initialMode,
    open,
  ]);
  const lastSeededKey = useRef(seedKey);
  const saveScope = useRef(0);
  const renderedSeed = useRef(seedKey);
  if (renderedSeed.current !== seedKey) {
    renderedSeed.current = seedKey;
    saveScope.current += 1;
  }
  useEffect(
    () => () => {
      saveScope.current += 1;
    },
    [],
  );
  // Chave cuja busca de previsto já foi disparada. É um ref, não estado: se o estado
  // `hydrated` entrasse nas dependências do efeito, marcar "carregando" o re-rodaria
  // e a limpeza cancelaria a própria busca que acabou de sair.
  const hydrationRequest = useRef(null);

  const queryLevel = targetType === "node" ? nodeLevel : targetType;

  // Alvo controlado pelo chamador: quando a chave da prop muda, o editor segue.
  useEffect(() => {
    const key = seedKey;
    if (lastSeededKey.current === key) return;
    lastSeededKey.current = key;
    setChosenTarget(seededTarget);
    // Alvo semeado NOVO leva o resto do formulário com ele: editar uma regra pela
    // lista (`onEditRule`) cai em "Personalizado" com o perfil GRAVADO, e abrir para
    // um alvo sem regra volta ao padrão — nunca sobra a forma do alvo anterior.
    setShortcut(seededProfile ? "custom" : "equal");
    setCustomLines(seedLines(seededProfile));
    setDescription(initialRule?.description ?? "");
    setStartMonth(initialRule?.startMonth ?? "");
    setDescriptionDirty(false);
    setAiDraft(null);
    setSaving(false);
    confirmationRequest.current = null;
    confirmedResult.current = null;
    setOverwriteWarning(false);
    setOverrideDecision(null);
    setMode(initialMode);
    setSaveError(null);
    setHydrated(null);
    setRuleSource(initialSource);
    hydrationRequest.current = null;
  }, [seedKey, seededTarget, seededProfile, initialRule, initialSource, initialMode]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  // Foco entra no painel ao abrir e volta pro gatilho ao fechar (espec. 8.5).
  useEffect(() => {
    if (!open) return undefined;
    restoreFocusRef.current = document.activeElement;
    panelRef.current?.focus();
    return () => {
      const previous = restoreFocusRef.current;
      restoreFocusRef.current = null;
      if (
        previous &&
        typeof previous.focus === "function" &&
        document.body.contains(previous)
      ) {
        previous.focus();
      }
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const timer = setTimeout(
      () => setDebouncedSearch(search.trim()),
      SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(timer);
  }, [open, search]);

  useEffect(() => {
    if (!open || chosenTarget || !workId) return undefined;
    // `cancelled` é a guarda contra resposta desatualizada: trocar o filtro enquanto
    // uma busca voa não pode deixar a resposta antiga sobrepor a mais recente.
    let cancelled = false;
    (async () => {
      setLoadingTargets(true);
      setTargetsError(false);
      const payload = await fetchDisbursementTargets(workId, {
        level: queryLevel,
        search: debouncedSearch,
      });
      if (cancelled) return;
      if (!payload || !Array.isArray(payload.items)) {
        setCandidates([]);
        setTargetsError(true);
        setLoadingTargets(false);
        return;
      }
      setCandidates(payload.items.map(normalizeTarget));
      setLoadingTargets(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, chosenTarget, workId, queryLevel, debouncedSearch]);

  // Alvo sem previsto na mão: busca a série do próprio alvo antes de pré-visualizar.
  // Sem isso a pré-visualização de uma regra existente sairia zerada — e um zero na
  // tela o cliente lê como resposta do motor, não como dado que faltou.
  useEffect(() => {
    if (!open || !workId || !chosenTarget) return undefined;
    if ((chosenTarget.plannedSeries?.length ?? 0) > 0) return undefined;
    const requestKey = JSON.stringify([workId, chosenTarget.key]);
    if (hydrationRequest.current === requestKey) return undefined;
    // `?level=` é OBRIGATÓRIO na rota de alvos (400 `invalid_target_level` sem ele) e
    // não aceita o nível de armazenamento `resource_group`: o nível de consulta é
    // derivado AQUI, na hora do fetch. Sem nível derivável não há consulta possível —
    // o alvo fica com a série que ele já tem (vazia) e o gráfico dá o veredito
    // honesto, em vez de gastar um 400 que morreria calado.
    const queryLevelForTarget = targetQueryLevel(chosenTarget);
    if (!queryLevelForTarget) {
      setHydrated({
        key: chosenTarget.key,
        series: chosenTarget.plannedSeries ?? [],
        status: "ready",
      });
      return undefined;
    }
    hydrationRequest.current = requestKey;
    let cancelled = false;
    setHydrated({ key: chosenTarget.key, series: [], status: "loading" });
    (async () => {
      const payload = await fetchDisbursementTargets(workId, {
        level: queryLevelForTarget,
        search:
          chosenTarget.kind === "node"
            ? chosenTarget.key.slice("node:".length, "node:".length + 80)
            : String(chosenTarget.label ?? "").slice(0, 80),
      });
      if (cancelled) return;
      if (!payload || !Array.isArray(payload.items)) {
        // Envelope de erro (ou `null` de rede) é FALHA, não "alvo sem previsto".
        setHydrated({ key: chosenTarget.key, series: [], status: "failed" });
        return;
      }
      const match = payload.items
        .map(normalizeTarget)
        .find((candidate) => candidate.key === chosenTarget.key);
      setHydrated({
        workId,
        key: chosenTarget.key,
        series: match?.plannedSeries ?? [],
        plannedCents: match?.plannedCents ?? null,
        status: "ready",
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [open, workId, chosenTarget]);

  const previewSeries = useMemo(() => {
    if ((chosenTarget?.plannedSeries?.length ?? 0) > 0)
      return chosenTarget.plannedSeries;
    return hydrated?.key && hydrated.key === chosenTarget?.key
      ? hydrated.series
      : [];
  }, [chosenTarget, hydrated]);

  // Sem obra não há o que buscar: cai direto no veredito honesto do gráfico em vez
  // de girar "carregando" para sempre.
  const previewHydration = useMemo(() => {
    if (!chosenTarget || !workId) return "ready";
    if ((chosenTarget.plannedSeries?.length ?? 0) > 0) return "ready";
    return hydrated?.key === chosenTarget.key ? hydrated.status : "loading";
  }, [chosenTarget, workId, hydrated]);

  // O primeiro mês com valor do alvo na origem: o início padrão do novo cronograma.
  const firstOriginMonth = useMemo(
    () =>
      (previewSeries ?? [])
        .filter((point) => point?.cents > 0)
        .map((point) => point?.month ?? point?.competence ?? null)
        .filter(Boolean)
        .sort()[0] ?? null,
    [previewSeries],
  );
  const effectiveStartMonth = startMonth || firstOriginMonth || null;

  // O perfil visível é SEMPRE a tabela: os atalhos só a preenchem (espec. 8.3). Assim o
  // cliente vê exatamente o que vai ser gravado, e o "Personalizado" é a mesma tabela.
  const visible = useMemo(() => {
    if (shortcut === "custom") return { lines: customLines, error: null };
    try {
      // Chaves determinísticas: `validateProfile` garante (unidade, defasagem) única no
      // gerado, então unidade + defasagem + atalho identifica a linha sem depender da
      // posição. Cada linha vira a forma interna uniforme `{ unit, offset, percentBps }`
      // (profile.js aceita `unit: "months"` com `offset` exatamente como `offsetMonths`),
      // então a tabela e o validador nunca precisam ramificar por forma.
      const lines = buildShortcutProfile(shortcut, form).map((line) => {
        const unit = line.unit ?? "months";
        const offset = line.unit === undefined ? line.offsetMonths : line.offset;
        return { lineId: `${shortcut}:${unit}:${offset}`, unit, offset, percentBps: line.percentBps };
      });
      return { lines, error: null };
    } catch (error) {
      return { lines: [], error: profileErrorText(error) };
    }
  }, [shortcut, form, customLines]);

  // `validateProfile`/`describeProfile` lançam: o resumo só é pedido depois de validar.
  const validation = useMemo(() => {
    if (visible.error)
      return { profile: null, message: visible.error, summary: null };
    try {
      const validated = validateProfile(
        visible.lines.map(({ unit, offset, percentBps }) => ({ unit, offset, percentBps })),
      );
      return {
        profile: validated,
        message: null,
        summary: describeProfile(validated),
      };
    } catch (error) {
      return { profile: null, message: profileErrorText(error), summary: null };
    }
  }, [visible]);

  // Só notifica com perfil VÁLIDO e só com o editor aberto: um offcanvas fechado não
  // tem por que empurrar perfil semeado para a página que o hospeda.
  useEffect(() => {
    if (!open || !validation.profile) return;
    onChangeProfile?.(validation.profile);
  }, [open, validation.profile, onChangeProfile]);

  // Modo apresentação é gate duro: um `readOnly={false}` do chamador não o destrava.
  const isReadOnly = readOnly || isPresentationProject(project);

  // A prévia dos serviços impactados (design D13): pedida com a forma VÁLIDA, só para alvos
  // de insumo, família ou grupo, e nunca em modo apresentação. Fica antes do retorno do
  // editor fechado — um hook não pode depender de o painel estar aberto.
  const needsImpact = requiresImpactPreview(chosenTarget);
  // Num alvo de EAP a prévia não trava o salvar, mas é ela que lista os insumos com regra
  // própria dentro do serviço/subetapa/etapa — por isso é pedida também para nós.
  const wantsImpact = needsImpact || chosenTarget?.kind === "node";
  // D20: o ajuste mês a mês só existe num alvo de EAP; o outro modo nem pede a prévia.
  const nodeTarget = chosenTarget?.kind === "node";
  // Somente leitura não edita nada: o modo do ajuste nem abre (nem fica "carregando").
  const monthMode = nodeTarget && mode === EDITOR_MODES.monthEdit && !isReadOnly;
  const impact = useRuleImpact({
    workId,
    target: chosenTarget ? ruleTargetOf(chosenTarget) : null,
    profile: validation.profile,
    startMonth: effectiveStartMonth,
    enabled: open && !isReadOnly && wantsImpact && !monthMode,
    debounceMs: impactDebounceMs,
  });
  const monthEdit = useMonthEdit({
    workId,
    wbsCodeId: nodeTarget ? chosenTarget.key.slice("node:".length) : null,
    enabled: open && !isReadOnly && monthMode,
  });

  if (!open) return null;
  const executionComplete = Boolean(confirmedResult.current);
  const titleText = monthMode
    ? "Ajuste mês a mês"
    : initialRule
      ? "Editar regra de desembolso"
      : "Nova regra de desembolso";
  // Para alvos com prévia, a ação principal só habilita com a prévia CALCULADA (ou com a
  // declaração de que a obra não tem origem utilizável para calcular).
  // Num alvo de EAP, salvar espera a verificação dos insumos com regra própria terminar
  // (D19): sem ela, a escolha entre manter e sobrescrever passaria despercebida. Uma falha
  // da verificação não trava — vale a precedência de hoje.
  const impactSettled = needsImpact
    ? impact.status === "ready" || impact.status === "unavailable"
    : !wantsImpact || impact.status !== "loading";
  const overrides =
    impact.status === "ready" ? impact.answer?.overrides ?? [] : [];
  const hasOverrides = overrides.length > 0;
  // A proposta de IA confirma pela rota da IA, que não desativa regras: ali vale manter.
  const canOverride = !aiDraft;
  let effectiveDecision = null;
  if (hasOverrides) {
    effectiveDecision = canOverride
      ? overrideDecision
      : OVERRIDE_DECISIONS.keep;
  }
  const decisionPending = hasOverrides && !effectiveDecision;
  const overriding = effectiveDecision === OVERRIDE_DECISIONS.override;
  const canSave =
    !isReadOnly &&
    !!chosenTarget &&
    !!validation.profile &&
    !saving &&
    impactSettled &&
    !decisionPending;
  const impactedCount = impact.answer?.totals?.impactedCount ?? 0;
  const monthCanSave = !isReadOnly && monthMode && monthEdit.canSave;
  const monthMissing = (() => {
    if (isReadOnly || !monthMode || monthEdit.saving || monthCanSave) return null;
    if (monthEdit.status === "loading" || monthEdit.status === "idle") return "Carregando a referência do alvo.";
    if (monthEdit.status !== "ready") return "A referência do alvo não está disponível.";
    if (monthEdit.balance !== 0) return "O saldo a redistribuir precisa fechar em zero.";
    return "Altere ao menos um mês para salvar o ajuste.";
  })();
  const missing = monthMode ? monthMissing : (() => {
    if (isReadOnly || saving || executionComplete || !chosenTarget || canSave) return null;
    if (!validation.profile) return "A forma de desembolso ainda não está completa e válida.";
    if (decisionPending)
      return "Escolha como tratar os insumos com regra própria neste alvo.";
    if (!needsImpact)
      return "Verificando os insumos com regra própria neste alvo.";
    if (impact.status === "error") return "Calcule o impacto novamente para reprogramar.";
    return "Aguardando o cálculo do impacto nos serviços.";
  })();
  const originName = origin?.id ? ORIGIN_NAMES[origin.id] : null;
  // A coluna de referência do ajuste diz de onde vêm os valores: a origem, ou a regra vigente.
  const monthReferenceLabel = monthEdit.data?.referenceKind === "rule"
    ? "Regra vigente"
    : originName ?? "Referência";
  // O ajuste mês a mês do PRÓPRIO alvo, descartado ao salvar um novo cronograma (D20).
  const targetAdjustments = !monthMode && impact.status === "ready"
    ? impact.answer?.targetAdjustments ?? null
    : null;
  const descendantAdjustedNodes = !monthMode && impact.status === "ready"
    ? impact.answer?.descendantAdjustedNodes ?? 0
    : 0;
  // Valor do item ajustado: o do alvo, ou o que a busca do previsto trouxe.
  const targetCents = chosenTarget
    ? chosenTarget.plannedCents ??
      (hydrated?.workId === workId && hydrated?.key === chosenTarget.key
        ? hydrated.plannedCents
        : null)
    : null;
  // O que a regra distribui de fato (D19): mantendo as regras próprias, o valor do alvo sem
  // as partes delas; sobrescrevendo, o valor global inteiro.
  const overriddenCents = overrides.reduce(
    (total, override) => total + (Number(override.redistributedCents) || 0),
    0,
  );
  const keepCents = Number.isFinite(targetCents)
    ? Math.max(0, targetCents - overriddenCents)
    : null;
  const ruleCents = hasOverrides && !overriding ? keepCents : targetCents;
  const scenarioSeries = impact.answer?.targetSeries ?? {};
  const resultingSeries = hasOverrides
    ? scenarioSeries[overriding ? "override" : "keep"] ?? null
    : null;

  // Espec. 4.1: escolher um alvo que JÁ tem regra carrega o perfil GRAVADO. Sobrescrever
  // em silêncio com o padrão à vista apagaria o contrato do cliente sem ele ver.
  const chooseTarget = (candidate) => {
    if (executionComplete) return;
    if (aiDraft && candidate?.key !== aiDraft.targetKey) {
      setSaveError("Descarte a proposta de IA antes de escolher outro alvo.");
      return;
    }
    setChosenTarget(candidate);
    setOverrideDecision(null);
    setSaveError(null);
    setRuleSource("manual");
    const stored = candidate?.ruleId
      ? rulesByRuleId?.[candidate.ruleId] ?? null
      : null;
    setStartMonth(stored?.startMonth ?? "");
    if (stored?.profile) {
      setCustomLines(seedLines(stored.profile));
      setShortcut("custom");
      setDescription(stored.description ?? "");
      setOverwriteWarning(false);
    } else {
      // Alvo com regra cuja forma não está à mão: o aviso é a pista de que salvar
      // aqui substitui o que está gravado.
      setOverwriteWarning(Boolean(candidate?.hasRule || candidate?.ruleId));
    }
    onChangeTarget?.(candidate);
  };

  // Proposta da IA (espec. 10.3, `DescribeRuleWithAi`): preenche alvo E perfil de uma
  // vez. O atalho SEMPRE vira "Personalizado" — sem isso a lógica de atalho (`visible`,
  // acima) regeneraria o perfil semeado a partir do `form` no próximo render, e o
  // perfil que a IA propôs nunca chegaria a aparecer na tela.
  const applyAiProposal = ({
    target: aiTarget,
    profile: aiProfile,
    draftId,
    version,
  }) => {
    if (isReadOnly) return;
    if (executionComplete) return;
    if (
      typeof draftId !== "string" ||
      !draftId ||
      !Number.isSafeInteger(version) ||
      version < 1
    ) {
      setSaveError("A proposta não possui um rascunho válido. Gere novamente.");
      return;
    }
    const normalized = normalizeTarget(aiTarget);
    if (normalized?.key !== chosenTarget?.key) {
      setDescription("");
      setDescriptionDirty(false);
    }
    setAiDraft({ draftId, version, targetKey: normalized.key, workId });
    confirmationRequest.current = null;
    confirmedResult.current = null;
    setChosenTarget(normalized);
    setCustomLines(seedLines(aiProfile));
    setShortcut("custom");
    setRuleSource("ai_assistant");
    setSaveError(null);
    setOverwriteWarning(false);
    hydrationRequest.current = null;
    setHydrated(null);
    onChangeTarget?.(normalized);
  };

  const updateCustomLine = (lineId, patch) => {
    if (executionComplete) return;
    setRuleSource("manual");
    setCustomLines((lines) =>
      lines.map((line) =>
        line.lineId === lineId ? { ...line, ...patch } : line,
      ),
    );
  };

  const addCustomLine = () => {
    if (executionComplete) return;
    setRuleSource("manual");
    setCustomLines((lines) => {
      // Teto do validador (`MAX_LINES`): recusar aqui é melhor que gravar e ouvir 400.
      if (lines.length >= MAX_LINES) return lines;
      const last = lines[lines.length - 1];
      return [
        ...lines,
        withLineId({
          unit: last?.unit ?? "months",
          offset: (Number.isFinite(last?.offset) ? last.offset : 0) + 1,
          percentBps: 0,
        }),
      ];
    });
  };

  const pickShortcut = (value) => {
    if (executionComplete) return;
    setRuleSource("manual");
    // Entrar no "Personalizado" leva a tabela que está na tela, não o padrão à vista.
    if (value === "custom" && shortcut !== "custom")
      setCustomLines(seedLines(validation.profile));
    setShortcut(value);
  };

  const save = async () => {
    if (!canSave) return;
    const scope = saveScope.current;
    setSaving(true);
    setSaveError(null);
    try {
      const trimmedDescription = description.trim();
      const payload = {
        target: ruleTargetOf(chosenTarget),
        profile: validation.profile,
        // O início efetivo (o escolhido ou o primeiro mês do alvo na origem): o que a prévia
        // mostrou é o que fica gravado.
        ...(effectiveStartMonth ? { startMonth: effectiveStartMonth } : {}),
        // Sempre manda `description`, mesmo vazia: é o único jeito de LIMPAR uma
        // descrição já gravada. O cliente da API já descarta apenas `undefined`,
        // então uma string vazia aqui grava vazio de propósito (não é omissão).
        description: trimmedDescription,
        // O carimbo das entradas em que a prévia foi calculada: se a obra mudou desde então
        // o servidor recusa (409) e a prévia é recalculada.
        ...(impact.answer?.previewStamp ? { previewStamp: impact.answer.previewStamp } : {}),
        // D19: aplicar esta regra a todo o valor desativa as regras próprias listadas.
        ...(overriding
          ? {
              overrideRuleIds: overrides
                .map((override) => override.ruleId)
                .filter(Boolean),
            }
          : {}),
      };
      // Chamador que grava por conta (contrato da Task 16) passa `onSave`; sem ele o
      // editor fala com a rota. O envelope é conferido nos DOIS caminhos: delegar não
      // é motivo para fechar o editor sobre uma gravação que não aconteceu.
      let result;
      if (aiDraft) {
        if (
          aiDraft.workId !== workId ||
          chosenTarget.key !== aiDraft.targetKey
        ) {
          setSaveError(
            "O alvo mudou. Gere outra proposta ou descarte esta para editar manualmente.",
          );
          return;
        }
        const body = {
          expectedVersion: aiDraft.version,
          targetKey: chosenTarget.key,
          profile: validation.profile,
          ...(descriptionDirty ? { description: trimmedDescription } : {}),
        };
        const fingerprint = JSON.stringify([workId, aiDraft.draftId, body]);
        if (confirmationRequest.current?.fingerprint !== fingerprint) {
          confirmationRequest.current = {
            fingerprint,
            idempotencyKey: newIdempotencyKey(),
          };
        }
        if (confirmedResult.current) {
          if (confirmedResult.current.fingerprint !== fingerprint)
            throw new Error(
              "A regra já foi salva. Feche e abra uma nova edição para alterar o conteúdo.",
            );
          result = confirmedResult.current.result;
        } else {
          const receipt = await confirmRuleWithAi(
            workId,
            aiDraft.draftId,
            body,
            { idempotencyKey: confirmationRequest.current.idempotencyKey },
          );
          if (saveScope.current !== scope) return;
          if (
            receipt?.status !== "executed" ||
            receipt.draftId !== aiDraft.draftId ||
            receipt.targetKey !== body.targetKey ||
            receipt.version !== aiDraft.version + 1 ||
            typeof receipt.ruleId !== "string" ||
            !receipt.ruleId ||
            !Number.isSafeInteger(receipt.rulesRevision) ||
            receipt.rulesRevision < 0
          ) {
            throw new Error("invalid_execution_receipt");
          }
          result = {
            ...receipt,
            code: receipt.regeneration?.status === "ready" ? 200 : 202,
          };
          confirmedResult.current = { fingerprint, result };
        }
      } else {
        result = onSave
          ? await onSave(payload)
          : await saveDisbursementRule(workId, {
              ...payload,
              source: ruleSource,
            });
      }
      if (saveScope.current !== scope) return;
      if (!result || (result.code !== 200 && result.code !== 202)) {
        setSaveError(saveErrorText(result));
        if (result?.errorCode === "impact_preview_stale") impact.refresh();
        return;
      }
      await onSaved?.(result);
      if (saveScope.current === scope) onClose?.();
    } catch (error) {
      // O `onSave` do chamador pode rejeitar (rede, 4xx/5xx): virar mensagem na tela
      // em vez de rejeição não tratada, e o botão volta a aceitar clique.
      if (saveScope.current === scope)
        setSaveError(
          aiDraft && error?.message !== "invalid_execution_receipt"
            ? error?.message ?? SAVE_ERROR_TEXT
            : SAVE_ERROR_TEXT,
        );
    } finally {
      if (saveScope.current === scope) setSaving(false);
    }
  };

  const saveMonthEdit = async () => {
    if (!monthCanSave) return;
    const scope = saveScope.current;
    setSaveError(null);
    try {
      const trimmedDescription = description.trim();
      const result = await monthEdit.save(trimmedDescription ? { description: trimmedDescription } : {});
      if (saveScope.current !== scope) return;
      if (!result || (result.code !== 200 && result.code !== 202)) {
        setSaveError(monthSaveErrorText(result));
        return;
      }
      await onSaved?.(result);
      if (saveScope.current === scope) onClose?.();
    } catch {
      if (saveScope.current === scope) setSaveError(MONTH_SAVE_ERROR_TEXT);
    }
  };

  const pickMode = (value) => {
    if (executionComplete || value === mode) return;
    setMode(value);
    setSaveError(null);
  };

  const inherited = chosenTarget?.inheritedFrom ?? null;
  const inheritedLabel =
    inherited && typeof inherited === "object"
      ? inherited.label ?? inherited.key
      : inherited;
  const startHint = (() => {
    if (startMonth) return "Competência da 1ª parcela (defasagem 0)";
    if (firstOriginMonth)
      return `Padrão: 1º mês do alvo na origem (${competenceText(firstOriginMonth)})`;
    return "Padrão: 1º mês do alvo na origem";
  })();
  const canEditForm = !isReadOnly && !executionComplete;
  // O ajuste mês a mês é gravado pela rota do módulo; a proposta da IA confirma uma REGRA.
  const offersModes = nodeTarget && canEditForm && !aiDraft;
  const modeSwitch = offersModes ? (
    <div
      className="ampli-rule-editor__shortcuts ampli-rule-editor__modes"
      role="group"
      aria-label="Como ajustar o desembolso do alvo"
    >
      {[
        [EDITOR_MODES.schedule, "Novo cronograma"],
        [EDITOR_MODES.monthEdit, "Ajustar mês a mês"],
      ].map(([value, label]) => (
        <button
          key={value}
          type="button"
          aria-pressed={mode === value}
          className={mode === value ? "active" : undefined}
          onClick={() => pickMode(value)}
        >
          {label}
        </button>
      ))}
    </div>
  ) : null;
  const startField = (
    <label className="ampli-rule-editor__field ampli-rule-editor__field--start">
      <span className="card-label">Início do novo cronograma</span>
      <input
        type="month"
        className="default-input"
        aria-label="Início do novo cronograma"
        aria-describedby="ampli-rule-editor-start-hint"
        value={effectiveStartMonth ?? ""}
        disabled={!canEditForm}
        onChange={(event) => {
          if (!canEditForm) return;
          setRuleSource("manual");
          setStartMonth(event.target.value);
        }}
      />
      <span className="ampli-rule-editor__hint" id="ampli-rule-editor-start-hint">
        {startHint}
      </span>
    </label>
  );

  return (
    <div className="offcanvas active">
      <div
        className="offcanvas-container large ampli-rule-editor"
        role="dialog"
        aria-modal="true"
        aria-label={titleText}
        tabIndex={-1}
        ref={panelRef}
      >
        <hgroup className="offcanvas-header ampli-rule-editor__header">
          <div>
            <h2>{titleText}</h2>
            <p className="ampli-rule-editor__lead">
              {monthMode
                ? "O ajuste mantém a distribuição do alvo e muda só os meses que você editar: o valor tirado de um mês vai para outro, e o total não muda."
                : "A regra substitui o cronograma do alvo: o valor global dele é redistribuído por completo a partir do início escolhido, e os itens abaixo seguem a mesma forma até que um alvo mais específico a sobrescreva."}
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Fechar">
            <i
              className="icon icon-width-24 icon-times background-accent"
              aria-hidden={true}
            ></i>
          </button>
        </hgroup>

        <div className="offcanvas-content ampli-rule-editor__content">
          {!chosenTarget ? (
            <RuleTargetPicker
              targetType={targetType}
              nodeLevel={nodeLevel}
              search={search}
              candidates={candidates}
              loading={loadingTargets}
              error={targetsError}
              onChangeTargetType={setTargetType}
              onChangeNodeLevel={setNodeLevel}
              onChangeSearch={setSearch}
              onChoose={chooseTarget}
            />
          ) : (
            <section aria-label="Forma de desembolso" className="ampli-rule-editor__steps">
              <section className="ampli-rule-editor__section">
                <h3>Alvo</h3>
                <div className="ampli-rule-editor__chosen">
                  <div className="ampli-rule-editor__chosen-main">
                    <strong className="ampli-rule-editor__target-label">
                      {chosenTarget.label}
                    </strong>
                    {chosenTarget.path ? (
                      <span className="ampli-rule-editor__target-path">
                        {chosenTarget.path}
                      </span>
                    ) : null}
                    {inheritedLabel ? (
                      <span className="ampli-rule-editor__inherited">
                        {`herdado de ${inheritedLabel}`}
                      </span>
                    ) : null}
                  </div>
                  <dl className="ampli-rule-editor__facts">
                    <div>
                      <dt>Valor global</dt>
                      <dd>
                        <TargetValue cents={targetCents} />
                      </dd>
                    </div>
                    {originName ? (
                      <div>
                        <dt>Origem vigente</dt>
                        <dd data-testid="rule-origin">{originName}</dd>
                      </div>
                    ) : null}
                  </dl>
                  {(!seededTarget && !executionComplete) ||
                  (aiDraft && !executionComplete) ? (
                    <div className="ampli-rule-editor__chosen-actions">
                      {!seededTarget && !executionComplete ? (
                        <button
                          type="button"
                          className="button button-outline small"
                          onClick={() => chooseTarget(null)}
                        >
                          Trocar alvo
                        </button>
                      ) : null}
                      {aiDraft && !executionComplete ? (
                        <button
                          type="button"
                          className="button button-ghost small"
                          disabled={saving}
                          onClick={() => {
                            setAiDraft(null);
                            setRuleSource("manual");
                            setSaveError(null);
                            confirmationRequest.current = null;
                            confirmedResult.current = null;
                          }}
                        >
                          Descartar proposta e editar manualmente
                        </button>
                      ) : null}
                    </div>
                  ) : null}
                </div>

                {overwriteWarning ? (
                  <p className="alert alert-info" role="status">
                    Este alvo já tem regra: salvar aqui substitui a forma atual.
                  </p>
                ) : null}
              </section>

              {monthMode ? (
                <>
                  <section className="ampli-rule-editor__section">
                    <h3>Como esse alvo é pago?</h3>
                    {modeSwitch}
                    <p className="ampli-rule-editor__section-lead">
                      {`Parte de ${monthEdit.data?.referenceKind === "rule" ? "a regra vigente do alvo" : originName ? `"${originName}"` : "a referência do alvo"}. Edite o valor de um mês, adie para o mês seguinte ou mova para outro mês; o saldo a redistribuir precisa fechar em zero.`}
                    </p>
                    {monthEdit.status === "loading" || monthEdit.status === "idle" ? (
                      <p className="card-label" role="status">Carregando a referência do alvo…</p>
                    ) : null}
                    {monthEdit.status === "unavailable" ? (
                      <p className="alert alert-info" role="status">{MONTH_UNAVAILABLE_TEXT}</p>
                    ) : null}
                    {monthEdit.status === "error" ? (
                      <div className="alert alert-error" role="alert">
                        <span>Não foi possível carregar a referência do alvo.</span>{" "}
                        <button type="button" className="button button-ghost small" onClick={monthEdit.reload}>
                          Tentar novamente
                        </button>
                      </div>
                    ) : null}
                    {monthEdit.status === "ready" && monthEdit.data?.originChangedSinceEdit ? (
                      <p className="alert alert-info" role="status" data-testid="month-edit-origin-changed">
                        A referência mudou desde o ajuste (a origem foi atualizada). Confira os meses antes de salvar.
                      </p>
                    ) : null}
                    {monthEdit.status === "ready" && (monthEdit.data?.overlapping?.length ?? 0) > 0 ? (
                      <p className="alert alert-info" role="status" data-testid="month-edit-overlapping">
                        {`Há ${monthEdit.data.overlapping.length === 1 ? "um ajuste" : `${monthEdit.data.overlapping.length} ajustes`} num nível acima ou abaixo deste alvo. Eles continuam valendo e podem disputar o valor dos mesmos meses.`}
                      </p>
                    ) : null}
                    {monthEdit.status === "ready" ? (
                      <MonthEditTable
                        edit={monthEdit}
                        referenceLabel={monthReferenceLabel}
                        readOnly={!canEditForm}
                        currentMonth={currentMonth ?? currentCompetence()}
                      />
                    ) : null}
                  </section>
                  {monthEdit.status === "ready" ? (
                    <section className="ampli-rule-editor__section">
                      <RulePreviewChart
                        plannedSeries={seriesPairs(monthEdit.reference).map(([month, cents]) => ({ month, cents }))}
                        profile={AT_SIGHT_PROFILE}
                        originName={monthReferenceLabel}
                        targetLabel={chosenTarget.label}
                        resultingSeries={seriesPairs(monthEdit.edited)}
                        resultingLabel="Ajustado"
                        showTable={false}
                      />
                    </section>
                  ) : null}
                </>
              ) : null}

              {/* D19: antes da forma, porque o valor que a forma divide depende da escolha. */}
              {!monthMode ? (
              <RuleOverridesSection
                impact={wantsImpact && !isReadOnly ? impact : null}
                onEditOverride={
                  onEditOverride && !executionComplete
                    ? (override) => onEditOverride(override)
                    : undefined
                }
                decision={effectiveDecision}
                onDecide={
                  executionComplete
                    ? undefined
                    : (value) => {
                        setOverrideDecision(value);
                        setSaveError(null);
                      }
                }
                keepCents={keepCents ?? 0}
                targetCents={targetCents ?? 0}
                canOverride={canOverride}
              />
              ) : null}

              {!monthMode ? (
              <section className="ampli-rule-editor__section">
                <h3>Como esse alvo é pago?</h3>
                {modeSwitch}
                {targetAdjustments?.monthCount > 0 ? (
                  <p className="alert alert-info" role="status" data-testid="rule-discards-month-edit">
                    {`Este alvo tem ajuste mês a mês em ${monthsText(targetAdjustments.monthCount)}. Salvar um novo cronograma descarta esse ajuste.`}
                  </p>
                ) : null}
                {descendantAdjustedNodes > 0 ? (
                  <p className="card-label" role="status" data-testid="rule-keeps-descendant-month-edits">
                    {`${descendantAdjustedNodes === 1 ? "Um item" : `${descendantAdjustedNodes} itens`} abaixo deste alvo ${descendantAdjustedNodes === 1 ? "tem" : "têm"} ajuste mês a mês. ${descendantAdjustedNodes === 1 ? "Ele continua" : "Eles continuam"} valendo e o que não couber na nova curva aparece como excedente.`}
                  </p>
                ) : null}
                <p className="ampli-rule-editor__section-lead">
                  Escolha o início e a forma. O valor global sai dos meses da
                  origem e é dividido pelas parcelas abaixo; num alvo com vários
                  itens, cada um recebe a mesma forma na proporção do seu valor.
                </p>

                {canEditForm ? (
                  <RuleShortcutFields
                    shortcut={shortcut}
                    form={form}
                    errorId={validation.message ? PROFILE_ERROR_ID : undefined}
                    onPick={pickShortcut}
                    onPatch={(patch) => {
                      setRuleSource("manual");
                      setForm((current) => ({ ...current, ...patch }));
                    }}
                    startField={startField}
                  />
                ) : (
                  <div className="ampli-rule-editor__shortcut-fields">{startField}</div>
                )}

                <RuleProfileTable
                  lines={visible.lines}
                  editable={canEditForm && shortcut === "custom"}
                  message={validation.message}
                  summary={validation.summary}
                  onChangeLine={updateCustomLine}
                  onRemoveLine={(lineId) => {
                    setRuleSource("manual");
                    setCustomLines((lines) =>
                      lines.filter((line) => line.lineId !== lineId),
                    );
                  }}
                  onAddLine={addCustomLine}
                  totalCents={ruleCents}
                  showZeroTotal={hasOverrides}
                  startMonth={effectiveStartMonth}
                />
              </section>
              ) : null}

              {needsImpact && !monthMode ? (
                <section className="ampli-rule-editor__section">
                  <RuleImpactSection
                    impact={impact}
                    readOnly={isReadOnly}
                    onRetry={impact.refresh}
                  />
                </section>
              ) : null}

              {!monthMode ? (
              <section className="ampli-rule-editor__section">
                {/* O título mora no gráfico: é ele que sabe se a obra está em R$ ou CUB. */}
                <RulePreviewChart
                  plannedSeries={previewSeries}
                  profile={validation.profile ?? visible.lines}
                  startMonth={effectiveStartMonth}
                  hydration={previewHydration}
                  originName={originName}
                  targetLabel={chosenTarget.label}
                  resultingSeries={resultingSeries}
                />
              </section>
              ) : null}

              {!isReadOnly ? (
                <section className="ampli-rule-editor__section">
                  <h3>{monthMode ? "Descrição" : "Descrição e IA"}</h3>
                  <label className="ampli-rule-editor__field">
                    <span className="card-label">Descrição (opcional)</span>
                    <input
                      type="text"
                      className="default-input"
                      aria-label="Descrição"
                      value={description}
                      maxLength={500}
                      disabled={executionComplete}
                      onChange={(event) => {
                        if (executionComplete) return;
                        setDescription(event.target.value);
                        setDescriptionDirty(true);
                      }}
                      placeholder="Contrato padrão de elevadores"
                    />
                  </label>

                  {/* Espec. 10.3: texto livre vira PROPOSTA de alvo + perfil; nada é
                      gravado aqui — só o botão principal do rodapé grava. */}
                  {!saving && !executionComplete && !monthMode ? (
                    <DescribeRuleWithAi
                      workId={workId}
                      targetKey={chosenTarget.key}
                      onFill={applyAiProposal}
                    />
                  ) : null}
                </section>
              ) : null}

              {saveError ? (
                <p className="alert alert-error" role="alert">
                  {saveError}
                </p>
              ) : null}
              {executionComplete ? (
                <p role="status">
                  A regra já foi salva. Atualize a tela para concluir; para
                  alterar o conteúdo, feche e abra uma nova edição.
                </p>
              ) : null}
            </section>
          )}
        </div>
        {chosenTarget ? (
          <footer className="ampli-rule-editor__footer">
            {missing ? (
              <p className="card-label ampli-rule-editor__missing" role="status">
                {missing}
              </p>
            ) : null}
            <button
              type="button"
              className="button button-ghost small"
              onClick={onClose}
            >
              {isReadOnly ? "Fechar" : "Cancelar"}
            </button>
            {!isReadOnly && monthMode ? (
              <button
                type="button"
                className="button button-accent small"
                disabled={!monthCanSave}
                onClick={saveMonthEdit}
              >
                {monthEdit.saving ? "Salvando…" : "Salvar ajuste"}
              </button>
            ) : null}
            {!isReadOnly && !monthMode ? (
              <button
                type="button"
                className="button button-accent small"
                disabled={!canSave}
                onClick={save}
              >
                {executionComplete
                  ? "Atualizar tela"
                  : impactedCount > 0
                    ? "Reprogramar desembolso"
                    : "Salvar regra"}
              </button>
            ) : null}
          </footer>
        ) : null}
      </div>
      <div onClick={onClose} className="offcanvas-background" />
    </div>
  );
};

export default RuleEditorOffcanvas;
